import { Divider, Group, Modal, Select, Stack, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import { DateInput } from 'components/inputs/DateInput';
import P2Regular from 'components/typography/P2Regular';
import panic from 'errors/panic';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface FaultFixValues {
  fixedAt: string;
  fixedNote?: string;
  fixedByUserId: number;
}

export interface FaultFixParams {
  deviceId: number;
  canResolveDeviceFaults: boolean;
  canResolveDeviceFaultsOnBehalf: boolean;
  onFaultFix: (data: FaultFixValues) => Promise<void>;
}

interface FixDeviceFaultModalProps extends FaultFixParams {
  opened: boolean;
  onClose: () => void;
}

/** Shared resolution form for self-fix and delegated resolution. */
export default function FixDeviceFaultModal({
  deviceId,
  canResolveDeviceFaults,
  canResolveDeviceFaultsOnBehalf,
  onFaultFix,
  opened,
  onClose,
}: FixDeviceFaultModalProps) {
  const { getAction, userId, firstName, lastName } = useApi();
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(canResolveDeviceFaultsOnBehalf);
  const [optionsError, setOptionsError] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [submitError, setSubmitError] = useState<string>();
  const now = useMemo(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
      2,
      '0'
    )}`;
  }, []);
  const form = useForm({
    initialValues: {
      fixedAt: now,
      fixedNote: '',
      fixedByUserId: canResolveDeviceFaults ? String(userId) : '',
    },
    validate: {
      fixedAt: (value) => (!value ? 'Vyplňte dátum riešenia' : null),
      fixedByUserId: (value) => (!value ? 'Vyberte riešiteľa nedostatku' : null),
    },
  });
  const choices = [
    ...(canResolveDeviceFaults ? [{ value: String(userId), label: `${firstName} ${lastName} (ja)` }] : []),
    ...options,
  ];
  const selectedSelf = form.values.fixedByUserId === String(userId) && canResolveDeviceFaults;
  const delegatedUnavailable = optionsLoading || optionsError || options.length === 0;

  useEffect(() => {
    if (!canResolveDeviceFaultsOnBehalf) return;
    let cancelled = false;
    getAction('DeviceFaultFixerGetOptions')({ parameters: { deviceId: String(deviceId) } })
      .then((users) => {
        if (!cancelled) {
          setOptions(
            users
              .filter((user) => user.userId !== userId)
              .map((user) => ({
                value: String(user.userId),
                label: user.fullName ?? `${user.firstName} ${user.lastName}`,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setOptionsError(true);
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canResolveDeviceFaultsOnBehalf, deviceId, getAction, userId]);

  /** Saves one resolution while keeping failed forms editable. */
  async function submit({ fixedAt, fixedNote, fixedByUserId }: typeof form.values) {
    if (savingRef.current) return;
    if (!selectedSelf && (delegatedUnavailable || !options.some((option) => option.value === fixedByUserId))) {
      form.setFieldError('fixedByUserId', 'Vyberte dostupného riešiteľa nedostatku');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSubmitError(undefined);
    try {
      await onFaultFix({ fixedAt, fixedNote, fixedByUserId: Number(fixedByUserId) });
      onClose();
    } catch (error: any) {
      setSubmitError('Riešenie sa nepodarilo uložiť. Overte oprávnenia a výber riešiteľa a skúste to znova.');
      panic(error);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  /** Blocks every dismissal path until the mutation finishes. */
  const close = () => {
    if (!savingRef.current) onClose();
  };

  return (
    <Modal
      centered
      onClose={close}
      opened={opened}
      title="Riešenie nedostatku"
      closeButtonProps={{ loading: saving }}
      closeOnClickOutside={!saving}
      closeOnEscape={!saving}
    >
      <Divider color="gray.1" mb={24} mt={12} />
      <form onSubmit={form.onSubmit(submit)}>
        <Stack spacing={24} pb={48}>
          <Select
            label="Riešiteľ nedostatku"
            placeholder={optionsLoading ? 'Načítavajú sa riešitelia…' : 'Vyberte riešiteľa'}
            data={choices}
            searchable
            disabled={saving || !canResolveDeviceFaultsOnBehalf}
            nothingFound="Žiadny dostupný riešiteľ"
            {...form.getInputProps('fixedByUserId')}
          />
          {canResolveDeviceFaultsOnBehalf && !optionsLoading && (optionsError || options.length === 0) && (
            <P2Regular color="gray.8">
              {optionsError
                ? 'Riešiteľov sa nepodarilo načítať. Zatvorte formulár a skúste to znova.'
                : 'Žiadny iný používateľ nemá oprávnenie riešiť nedostatky tohto zariadenia.'}
            </P2Regular>
          )}
          <DateInput label="Dátum riešenia nedostatku" size="lg" disabled={saving} {...form.getInputProps('fixedAt')} />
          <Textarea
            label="Popis riešenia"
            placeholder="Popis riešenia"
            size="lg"
            disabled={saving}
            name="objekto-fixedNote"
            {...form.getInputProps('fixedNote')}
          />
          {submitError && <P2Regular color="red.6">{submitError}</P2Regular>}
        </Stack>
        <Group position="apart" spacing={16} pt={24}>
          <Button type="button" variant="subtle-gray" onClick={close} disabled={saving} px={8}>
            Zrušiť
          </Button>
          <Button type="submit" variant="primary" loading={saving} disabled={!selectedSelf && delegatedUnavailable}>
            Uložiť a označiť ako vyriešené
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
